import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { Content, Header } from "./index";
import { useDispatch, useSelector, useStore } from "react-redux";
import { GetCompanyList } from "../graphql/dashboard";
import {
  getUpdateCompanyNeeded,
  importCompanyList,
  importSelectedCompany,
} from "../redux/actions/companyList";
import { importUpdateNeeded } from "../redux/actions/flujocaja";
import { importAlertState } from "../redux/actions/alert";
import { updateModulePermissions } from "../redux/actions/auth";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Grid } from 'semantic-ui-react'
import "./layout.scss";
import { CompanyContext, FlowContext, UserContext, ObligationsContext, EOAFContext } from "src/context";
import { GetUserById, GetMyPermissions } from "src/graphql/users";
import { NewSidebar } from "./Sidebar/NewSidebar";
import { GetFlowStructure } from "src/graphql/cashflow";
import { GetObligations } from "src/graphql/obligations";
import { setObligationCatalog } from "src/components/general/js/forms/reconcile/components/matchRowHelpers";
import { GetEOAFStructureTree } from "src/graphql/eoaf";
import DocumentDetailContainer from "../components/documentostributarios/DocumentDetailContainer";
import { ActiveBannersProvider } from "../components/banners/ActiveBannersContext";
import AppBanners from "../components/banners/AppBanners";
import AnnouncementModal from "../components/banners/AnnouncementModal";
import { AccountsProvider } from "../context/AccountsContext";
import { MasterStructuresProvider } from "../context/MasterStructuresContext";
import { RulesProvider } from "../context/RulesContext";
import JournalEntryRuleSaveHost from "../components/general/js/forms/reconcile/contabilidad/JournalEntryRuleSaveHost";
import { useSelectedCompanySync } from "../hooks/useSelectedCompanySync";
import {
  readStoredCompanyId,
  clearStoredCompanyId,
  writeStoredCompanyIdOnly,
} from "../utils/selectedCompanySync";
import {
  readCompanyListCache,
  writeCompanyListCache,
  pickCompanyFromList,
} from "../utils/companyListCache";
import i18n from "../i18n";
import {
  canUseJapaneseLanguage,
  isAppLanguage,
  writeStoredAppLanguage,
} from "../utils/appLanguage";

const VISIBILITY_REFETCH_COMPANIES_MS = 45000;

const syncAppHeaderHeight = () => {
  const header = document.querySelector('.c-header');
  const height = header?.offsetHeight || 72;
  document.documentElement.style.setProperty('--app-header-height', `${height}px`);
};

/** getEOAFStructure returns [EOAFStructureNode] — not a single root with .children */
function normalizeEOAFStructureRoots(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.children)) return raw.children;
  if (raw && raw.key) return [raw];
  return [];
}

const Layout = (props) => {
  const dispatch = useDispatch()
  const store = useStore()
  const isMobile = useMediaQuery("(max-width:768px)");
  const [showCompanyList, setShowCompanyList] = useState(false)
  const [hideCompanyList] = useState(true)
  const [maximized, setMaximized] = useState(false)
  const [isFixed, setIsFixed] = useState(false);
  const [obligations, setObligations] = useState([]);
  const hasCompanyMounted = useRef(false);

  const userId = JSON.parse(localStorage.getItem("user"))

  const { fetching, data, reexecuteQuery, ..._rest } = GetCompanyList()
  const { data: userInfo, executeQuery: refetchUserInfo } = GetUserById({ id: userId?.uid })
  const { data: permissionsData, reexecuteQuery: refetchPermissions } = GetMyPermissions()

  const { updateNeeded } = useSelector((state) => state.flujocaja)
  const { selectedCompany, updateCompanyNeeded, companyList: companyListFromStore } = useSelector((state) => state.companyList)
  const { updateCashflowNeeded } = useSelector((state) => state.flujocaja)
  const { data: flowStructureData, reexecuteFlow, fetching: _flowStructureFetching } = GetFlowStructure({ companyId:selectedCompany?.id })
  const { data: eoafStructureData, fetching: eoafFetching, error: eoafError, reexecuteQuery: reexecuteEOAF } = GetEOAFStructureTree({ companyId: selectedCompany?.id || '' })
  // Eager-load del catálogo de obligaciones (antes lo poblaban perezosamente el dropdown/EOAF).
  // Es la fuente canónica para resolver labels de obligación en conciliación (ver matchRowHelpers).
  const obligationsInput = selectedCompany?.id ? { companyId: selectedCompany.id } : undefined
  const { data: obligationsCatalogData } = GetObligations(obligationsInput)

  const companyListArray = Array.isArray(companyListFromStore) ? companyListFromStore : []
  useSelectedCompanySync(dispatch, companyListArray, selectedCompany?.id)

  const cacheHydratedRef = useRef(false)
  const lastVisibilityCompaniesFetchRef = useRef(0)

  useLayoutEffect(() => {
    syncAppHeaderHeight();
    const header = document.querySelector('.c-header');
    if (!header || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(syncAppHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const uid = JSON.parse(localStorage.getItem("user") || "null")?.uid
    if (!uid) return

    const { companyList, selectedCompany: sel } = store.getState().companyList
    const listNotReady = !Array.isArray(companyList) || companyList.length === 0
    if (!listNotReady) return

    const cached = readCompanyListCache(uid)
    if (!cached?.length) return
    if (cacheHydratedRef.current) return

    cacheHydratedRef.current = true
    dispatch(importCompanyList(cached))

    const storedId = readStoredCompanyId()
    const { chosen, shouldClearStored } = pickCompanyFromList(
      cached,
      storedId,
      sel?.id
    )
    if (shouldClearStored) clearStoredCompanyId()

    if (chosen?.id) {
      dispatch(importSelectedCompany(chosen))
      const effectiveId = String(chosen.id)
      if (readStoredCompanyId() !== effectiveId) {
        writeStoredCompanyIdOnly(effectiveId)
      }
    }
  }, [dispatch, store])

  useEffect(() => {
    if (hasCompanyMounted.current) {
      reexecuteFlow({ requestPolicy: 'network-only' })
      if (selectedCompany?.id) {
        reexecuteEOAF({ requestPolicy: 'network-only' })
      }
    }
    hasCompanyMounted.current = true
  }, [selectedCompany?.id])

  useEffect(() => {
    const lang = userInfo?.User?.preferredLanguage;
    if (!isAppLanguage(lang) || i18n.language === lang) return;
    // No aplicar una preferencia en japonés si la cuenta ya no está habilitada:
    // quedaría la UI en japonés con el selector sin esa opción.
    if (lang === "ja" && !canUseJapaneseLanguage(userInfo?.User?.email)) return;
    writeStoredAppLanguage(lang);
    i18n.changeLanguage(lang);
  }, [userInfo?.User?.preferredLanguage, userInfo?.User?.email]);

  // Cargar permisos frescos del usuario y actualizar Redux
  useEffect(() => {
    if (permissionsData?.getMyPermissions) {
      dispatch(updateModulePermissions(permissionsData.getMyPermissions))
    }
  }, [permissionsData?.getMyPermissions, dispatch])

  // Refrescar permisos al inicio
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refetchPermissions({ requestPolicy: 'network-only' })
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if(updateCashflowNeeded?.value) reexecuteFlow({ requestPolicy: 'network-only' })
  }, [updateCashflowNeeded?.value])

  useEffect(() => {
    if (fetching || !data?.companiesByUser?.length) return

    const list = data.companiesByUser
    dispatch(importCompanyList(list))

    const uid = JSON.parse(localStorage.getItem("user") || "null")?.uid
    if (uid) writeCompanyListCache(uid, list)

    const storedId = readStoredCompanyId()
    const { chosen, shouldClearStored } = pickCompanyFromList(
      list,
      storedId,
      selectedCompany?.id
    )
    if (shouldClearStored) clearStoredCompanyId()

    if (chosen?.id) {
      dispatch(importSelectedCompany(chosen))
      const effectiveId = String(chosen.id)
      if (readStoredCompanyId() !== effectiveId) {
        writeStoredCompanyIdOnly(effectiveId)
      }
    }
  }, [fetching, data?.companiesByUser, dispatch, selectedCompany?.id])

  // Solo refetch companiesByUser cuando hace falta actualizar la lista de empresas
  // (updateCompanyNeeded). updateNeeded es para flujo/otros y no debe disparar
  // una nueva consulta de empresas en cada módulo (ej. F29 al calcular periodo).
  useEffect(() => {
    if (updateNeeded.value) {
      dispatch(importUpdateNeeded({ value: false }))
    }

    if (updateCompanyNeeded?.value) {
      reexecuteQuery({ requestPolicy: 'network-only' })
      dispatch(getUpdateCompanyNeeded({ value: false }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNeeded?.value, updateCompanyNeeded?.value])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refetchUserInfo({ requestPolicy: 'network-only' })
  }, [refetchUserInfo])

  // Deep-link: if the URL carries ?companyId=... (e.g. from an email notification),
  // auto-switch to that company once the companyList is available. Only runs when the
  // requested company differs from the currently selected one and is in the user's list.
  const urlCompanyHandledIdRef = useRef('')
  useEffect(() => {
    if (!Array.isArray(companyListArray) || companyListArray.length === 0) return
    try {
      const params = new URLSearchParams(window.location.search)
      const requestedId = params.get('companyId')
      if (!requestedId) return
      if (urlCompanyHandledIdRef.current === String(requestedId)) return
      if (selectedCompany?.id && String(selectedCompany.id) === String(requestedId)) {
        urlCompanyHandledIdRef.current = String(requestedId)
        return
      }
      const match = companyListArray.find(
        (c) => String(c?.id) === String(requestedId)
      )
      if (match) {
        urlCompanyHandledIdRef.current = String(requestedId)
        dispatch(importSelectedCompany(match, { userInitiated: true }))
        return
      }
      urlCompanyHandledIdRef.current = String(requestedId)
      dispatch(
        importAlertState({
          content: 'No tienes acceso a esta empresa',
          state: 'error',
          duration: 4,
        })
      )
    } catch (e) {
      // ignore
    }
  }, [companyListArray, selectedCompany?.id, dispatch])

  // Refrescar permisos y usuario al volver a la pestaña; lista de empresas con throttle
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      const now = Date.now()
      refetchPermissions({ requestPolicy: 'network-only' })
      refetchUserInfo({ requestPolicy: 'network-only' })

      if (now - lastVisibilityCompaniesFetchRef.current >= VISIBILITY_REFETCH_COMPANIES_MS) {
        lastVisibilityCompaniesFetchRef.current = now
        reexecuteQuery({ requestPolicy: 'network-only' })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [reexecuteQuery, refetchPermissions, refetchUserInfo])

  // Polling: permisos y datos de usuario (cambios admin/blockedFeatures); lista de empresas menos frecuente
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      refetchPermissions({ requestPolicy: 'network-only' })
      refetchUserInfo({ requestPolicy: 'network-only' })
    }, 90000)

    return () => clearInterval(pollInterval)
  }, [refetchPermissions, refetchUserInfo])

  useEffect(() => {
    const pollCompanies = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      reexecuteQuery({ requestPolicy: 'network-only' })
    }, 300000)

    return () => clearInterval(pollCompanies)
  }, [reexecuteQuery])

  // Context: mismo origen que consumidores que usan Redux — lista desde cache/hidratación
  // hasta que GraphQL devuelva datos; entonces prevalece la red (alineado con el efecto que importa a Redux).
  const contextCompanyList = React.useMemo(() => {
    const fromNetwork = data?.companiesByUser
    if (Array.isArray(fromNetwork) && fromNetwork.length > 0) {
      return fromNetwork
    }
    return companyListArray
  }, [data?.companiesByUser, companyListArray])

  const contextInformation = React.useMemo(
    () => ({
      companyList: contextCompanyList,
      selectedCompany,
    }),
    [contextCompanyList, selectedCompany]
  )

  useEffect(() => {
    const list = obligationsCatalogData?.getObligations
    if (Array.isArray(list) && list.length > 0) {
      setObligations(list)
      // Primar el catálogo usado para resolver type -> typeLabel (getObligationTypeDisplayLabel)
      // en TODAS las vistas (modal de detalle, conciliación, etc.), no solo en MatchList.
      setObligationCatalog(list)
    }
  }, [obligationsCatalogData?.getObligations, selectedCompany?.id])

  const obligationsContextValue = {
    obligations,
    setObligations,
  }

  const eoafContextValue = React.useMemo(
    () => ({
      structureTree: normalizeEOAFStructureRoots(eoafStructureData?.getEOAFStructure),
      loading: eoafFetching,
      error: eoafError,
      refetch: reexecuteEOAF,
    }),
    [eoafStructureData?.getEOAFStructure, eoafFetching, eoafError, reexecuteEOAF]
  )

  // Verificar si la empresa tiene estado bloqueado o con pagos pendientes
  const companyAdminStatus = selectedCompany?.adminStatus
  const isCompanyBlocked = companyAdminStatus === 'BLOQUEADO'
  const isCompanyPaymentPending = companyAdminStatus === 'ROJO'
  const hasCompanyRestriction = isCompanyBlocked || isCompanyPaymentPending

  // Banner elegante de restricción de empresa
  const CompanyRestrictionBanner = () => {
    if (!hasCompanyRestriction) return null

    const bannerConfig = {
      BLOQUEADO: {
        icon: '⊘',
        title: 'Acceso suspendido',
        message: 'Esta empresa ya no tiene acceso activo.',
        gradient: 'linear-gradient(135deg, #5AADA5 0%, #7EC8C0 100%)',
        accentColor: '#5AADA5',
      },
      ROJO: {
        icon: '◐',
        title: 'Cuenta pendiente',
        message: 'Regulariza tu cuenta para continuar.',
        gradient: 'linear-gradient(135deg, #E8534A 0%, #C93E36 100%)',
        accentColor: '#E8534A',
      },
    }

    const config = bannerConfig[companyAdminStatus] || bannerConfig.ROJO

    return (
      <div
        style={{
          position: 'fixed',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          animation: 'slideDown 0.4s ease-out',
        }}
      >
        <style>
          {`
            @keyframes slideDown {
              from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          `}
        </style>
        <div
          style={{
            background: config.gradient,
            borderRadius: '16px',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.1) inset',
            backdropFilter: 'blur(10px)',
            minWidth: '320px',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              color: 'white',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            {config.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                color: 'white',
                fontWeight: '600',
                fontSize: '15px',
                marginBottom: '2px',
                letterSpacing: '-0.01em',
              }}
            >
              {config.title}
            </div>
            <div
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '13px',
              }}
            >
              {config.message}
            </div>
          </div>
          <a
            href="mailto:soporte@smartcfo.cl"
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '500',
              textDecoration: 'none',
              transition: 'background-color 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            Contactar
          </a>
        </div>
      </div>
    )
  }

  return (
    <UserContext.Provider value={userInfo?.User || {}}>
      <CompanyContext.Provider value={contextInformation}>
        <AccountsProvider>
          <MasterStructuresProvider>
          <RulesProvider>
            <EOAFContext.Provider value={eoafContextValue}>
            <FlowContext.Provider value={flowStructureData?.getFlowStructure ? flowStructureData?.getFlowStructure[0] || {} : {}}>
              <ObligationsContext.Provider value={obligationsContextValue}>
                {/* Banner de restricción si aplica */}
                <CompanyRestrictionBanner />
                <div
                  className="c-app c-default-layout"
                  style={{ backgroundColor: "#F5F7FA" }}
                >

          <div className="app-layout-top">
            <Grid className="p-0 m-0">
              <Header
                location={props.location}
                isFixed={isFixed}
                isMobile={isMobile} />
            </Grid>
          </div>
          <ActiveBannersProvider>
            <AnnouncementModal />

          {/* <Sidebar
          showCompanyList={showCompanyList}
          setShowCompanyList={setShowCompanyList}
          hideCompanyList={hideCompanyList}
          setHideCompanyList={setHideCompanyList}
          maximized={maximized}
          setMaximized={setMaximized}
        /> */}
          <NewSidebar
            isFixed={isFixed}
            setIsFixed={setIsFixed}
          />

          <div
            className="c-wrapper contentWidth ml-0"
            style={{
              paddingLeft: isMobile ? "0px" : "84px",
              boxSizing: "border-box",
            }}
          >
            <AppBanners sidebarOffset={isMobile ? 0 : (isFixed ? 256 : 0)} />
            <div
              className="c-body contentML"
              style={{
                transition: "0.5s",
                overflowX: "hidden",
              }}
            >
              <Content
                maximized={maximized}
                setMaximized={setMaximized}
                showCompanyList={showCompanyList}
                setShowCompanyList={setShowCompanyList}
                hideCompanyList={hideCompanyList}
                isFixed={isFixed}
                isMobile={isMobile}
              />
            </div>
          </div>
          </ActiveBannersProvider>

          {/* Global Document Detail Modal */}
          <DocumentDetailContainer />
          <JournalEntryRuleSaveHost />
                </div>
              </ObligationsContext.Provider>
            </FlowContext.Provider>
            </EOAFContext.Provider>
          </RulesProvider>
          </MasterStructuresProvider>
        </AccountsProvider>
      </CompanyContext.Provider>
    </UserContext.Provider>
  );
};

export default Layout;
